/**
 * Browser-safe normalization helper.
 *
 * IMPORTANT:
 * This is only a syntactic pre-check.
 *
 * The Windows Agent performs authoritative filesystem realpath
 * validation immediately before process execution.
 */
export function normalizePathForComparison(p) {
    if (!p)
        return '';
    let normalized = p
        .trim()
        .replace(/\\/g, '/');
    const isWindowsDrive = /^[a-zA-Z]:/.test(normalized);
    let drivePrefix = '';
    if (isWindowsDrive) {
        drivePrefix =
            normalized.slice(0, 2).toLowerCase();
        normalized =
            normalized.slice(2);
    }
    const segments = normalized.split('/');
    const resolved = [];
    for (const segment of segments) {
        if (segment === '' ||
            segment === '.') {
            continue;
        }
        if (segment === '..') {
            if (resolved.length > 0) {
                resolved.pop();
            }
            continue;
        }
        resolved.push(segment);
    }
    let result = drivePrefix
        ? `${drivePrefix}/${resolved.join('/')}`
        : normalized.startsWith('/')
            ? `/${resolved.join('/')}`
            : resolved.join('/');
    if (!result.endsWith('/')) {
        result += '/';
    }
    return result;
}
/**
 * Syntactic containment validation.
 *
 * Prevents normal ../ traversal and sibling-prefix attacks such as:
 *
 * allowed:
 *   F:\KAIZEN REMOTE
 *
 * malicious sibling:
 *   F:\KAIZEN REMOTE EVIL
 *
 * Real filesystem symlink/junction protection is performed by the agent.
 */
export function isPathInAllowedRoots(targetPath, allowedRoots) {
    if (!targetPath ||
        !allowedRoots ||
        allowedRoots.length === 0) {
        return false;
    }
    const normalizedTarget = normalizePathForComparison(targetPath);
    return allowedRoots.some(root => {
        const normalizedRoot = normalizePathForComparison(root);
        return normalizedTarget.startsWith(normalizedRoot);
    });
}
/**
 * Validate a job payload before an execution service sees it.
 *
 * This is validation, NOT command sandboxing.
 *
 * KAIZEN Remote intentionally supports arbitrary operator-approved
 * PowerShell development commands.
 */
export function validateJobPayload(type, payload) {
    if (payload === null ||
        typeof payload !== 'object' ||
        Array.isArray(payload)) {
        return {
            valid: false,
            error: 'Payload must be an object'
        };
    }
    const value = payload;
    if (value.timeoutMs !== undefined &&
        (typeof value.timeoutMs !== 'number' ||
            !Number.isFinite(value.timeoutMs) ||
            value.timeoutMs < 1000 ||
            value.timeoutMs > 3_600_000)) {
        return {
            valid: false,
            error: 'timeoutMs must be between 1000 and 3600000 milliseconds'
        };
    }
    if (value.command !== undefined &&
        (typeof value.command !== 'string' ||
            value.command.length > 250_000)) {
        return {
            valid: false,
            error: 'command must be a string no larger than 250000 characters'
        };
    }
    if (value.scriptContent !== undefined &&
        (typeof value.scriptContent !== 'string' ||
            value.scriptContent.length > 1_000_000)) {
        return {
            valid: false,
            error: 'scriptContent must be a string no larger than 1000000 characters'
        };
    }
    if (value.args !== undefined &&
        (!Array.isArray(value.args) ||
            value.args.some(arg => typeof arg !== 'string'))) {
        return {
            valid: false,
            error: 'args must contain strings only'
        };
    }
    if (value.env !== undefined &&
        (!value.env ||
            typeof value.env !== 'object' ||
            Array.isArray(value.env) ||
            Object.entries(value.env).some(([key, envValue]) => !key ||
                typeof envValue !== 'string'))) {
        return {
            valid: false,
            error: 'env must be a string-to-string object'
        };
    }
    switch (type) {
        case 'powershell_command': {
            if (!value.command ||
                !value.command.trim()) {
                return {
                    valid: false,
                    error: 'powershell_command requires payload.command'
                };
            }
            break;
        }
        case 'powershell_script': {
            if (!value.scriptContent?.trim() &&
                !value.command?.trim()) {
                return {
                    valid: false,
                    error: 'powershell_script requires scriptContent or command'
                };
            }
            break;
        }
        /*
         * KAIZEN PC CONTROL
         *
         * This is deliberately an allow-list.
         *
         * Arbitrary shell commands, executable paths and PowerShell supplied
         * by the browser are NOT accepted through pc_control.
         */
        case 'pc_control': {
            const KAIZEN_PC_CONTROL_ALLOWED_ACTIONS = new Set([
                'pc.bootstrap',
                'apps.catalog',
                'apps.running',
                'apps.launch',
                'apps.close',
                'apps.forceClose',
                'favorites.list',
                'favorites.add',
                'favorites.remove',
                'power.lock',
                'power.monitorOff',
                'power.monitorOn',
                'power.sleep',
                'power.hibernate',
                'power.signout',
                'power.restart',
                'power.shutdown',
                'power.abortShutdown',
                'system.showDesktop',
                'system.restartExplorer',
                'system.diskUsage',
                'system.networkStatus'
            ]);
            if (typeof value.pcAction !== 'string' ||
                !KAIZEN_PC_CONTROL_ALLOWED_ACTIONS.has(value.pcAction)) {
                return {
                    valid: false,
                    error: 'pc_control contains an unsupported action'
                };
            }
            if (value.pcTargetId !== undefined &&
                typeof value.pcTargetId !== 'string') {
                return {
                    valid: false,
                    error: 'pc_control pcTargetId must be a string'
                };
            }
            if (value.pcPid !== undefined &&
                (typeof value.pcPid !== 'number' ||
                    !Number.isInteger(value.pcPid) ||
                    value.pcPid <= 0)) {
                return {
                    valid: false,
                    error: 'pc_control pcPid must be a positive integer'
                };
            }
            break;
        }
        case 'source_control': {
            const operations = new Set([
                'status',
                'fetch',
                'compare',
                'changes',
                'diff',
                'pull',
                'publish',
                'conflicts',
                'abort_merge',
                'abort_rebase',
                'branches',
                'create_branch',
                'switch_branch',
                'history',
                'github_auth',
                'create_pull_request'
            ]);
            if (typeof value.sourceControlOperation !== 'string' ||
                !operations.has(value.sourceControlOperation)) {
                return {
                    valid: false,
                    error: 'source_control contains an unsupported operation'
                };
            }
            const options = value.sourceControlOptions;
            if (options !== undefined &&
                (!options ||
                    typeof options !== 'object' ||
                    Array.isArray(options))) {
                return {
                    valid: false,
                    error: 'sourceControlOptions must be an object'
                };
            }
            if (options && typeof options === 'object') {
                const typedOptions = options;
                const stringFields = [
                    'branchName',
                    'filePath',
                    'commitMessage',
                    'description',
                    'pullRequestTitle',
                    'pullRequestBody'
                ];
                if (stringFields.some(field => (typedOptions[field] !== undefined &&
                    typeof typedOptions[field] !== 'string'))) {
                    return {
                        valid: false,
                        error: 'source-control text options must be strings'
                    };
                }
                if (typedOptions.selectedPaths !== undefined &&
                    (!Array.isArray(typedOptions.selectedPaths) ||
                        typedOptions.selectedPaths.length > 1000 ||
                        typedOptions.selectedPaths.some(item => typeof item !== 'string'))) {
                    return {
                        valid: false,
                        error: 'selectedPaths must contain at most 1000 strings'
                    };
                }
                if (typedOptions.limit !== undefined &&
                    (typeof typedOptions.limit !== 'number' ||
                        !Number.isInteger(typedOptions.limit) ||
                        typedOptions.limit < 1 ||
                        typedOptions.limit > 100)) {
                    return {
                        valid: false,
                        error: 'history limit must be an integer from 1 to 100'
                    };
                }
                if (typedOptions.diffMode !== undefined &&
                    typedOptions.diffMode !== 'unstaged' &&
                    typedOptions.diffMode !== 'staged') {
                    return {
                        valid: false,
                        error: 'diffMode must be unstaged or staged'
                    };
                }
                if (typedOptions.createPullRequest !== undefined &&
                    typeof typedOptions.createPullRequest !== 'boolean') {
                    return {
                        valid: false,
                        error: 'createPullRequest must be boolean'
                    };
                }
            }
            break;
        }
        case 'deploy_preview':
        case 'deploy_production': {
            if (!value.projectName ||
                typeof value.projectName !== 'string' ||
                !value.projectName.trim()) {
                return {
                    valid: false,
                    error: `${type} requires projectName`
                };
            }
            break;
        }
        case 'build':
        case 'test':
        case 'diagnostics':
            break;
        default:
            return {
                valid: false,
                error: `Unsupported job type: ${String(type)}`
            };
    }
    return {
        valid: true
    };
}
/**
 * Mask potentially sensitive paths in privacy/focus mode.
 */
export function sanitizePathForPrivacy(rawPath, privacyMode) {
    if (!privacyMode ||
        !rawPath) {
        return rawPath;
    }
    let sanitized = rawPath.replace(/([a-zA-Z]:[/\\]Users[/\\])[^/\\]+/i, '$1***');
    const parts = sanitized.split(/[/\\]/);
    if (parts.length > 3) {
        const driveOrRoot = parts[0];
        const basename = parts[parts.length - 1] ||
            parts[parts.length - 2];
        return `${driveOrRoot}/.../${basename}`;
    }
    return sanitized;
}
//# sourceMappingURL=security.js.map